import api from './api';

/**
 * WebAuthn/FIDO2 Service
 * Handles security key registration and authentication
 */

// Convert ArrayBuffer to Base64URL string
const bufferToBase64URL = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

// Convert Base64URL string to ArrayBuffer
const base64URLToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Parse the credential creation options from the server
const parseCreationOptions = (options) => {
  // Parse JSON if it's a string
  const opts = typeof options === 'string' ? JSON.parse(options) : options;

  return {
    ...opts,
    challenge: base64URLToBuffer(opts.challenge),
    user: {
      ...opts.user,
      id: base64URLToBuffer(opts.user.id),
    },
    excludeCredentials: (opts.excludeCredentials || []).map(cred => ({
      ...cred,
      id: base64URLToBuffer(cred.id),
    })),
  };
};

// Parse the credential request options from the server
const parseRequestOptions = (options) => {
  const opts = typeof options === 'string' ? JSON.parse(options) : options;

  return {
    ...opts,
    challenge: base64URLToBuffer(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(cred => ({
      ...cred,
      id: base64URLToBuffer(cred.id),
    })),
  };
};

// Format credential for sending to server
const formatCredentialForServer = (credential) => {
  const response = credential.response;

  const formatted = {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64URL(response.clientDataJSON),
    },
  };

  // Registration response has attestationObject
  if (response.attestationObject) {
    formatted.response.attestationObject = bufferToBase64URL(response.attestationObject);
  }

  // Authentication response has authenticatorData and signature
  if (response.authenticatorData) {
    formatted.response.authenticatorData = bufferToBase64URL(response.authenticatorData);
  }
  if (response.signature) {
    formatted.response.signature = bufferToBase64URL(response.signature);
  }
  if (response.userHandle) {
    formatted.response.userHandle = bufferToBase64URL(response.userHandle);
  }

  return formatted;
};

export const webauthnService = {
  /**
   * Check if WebAuthn is supported in the browser
   */
  isSupported: () => {
    return window.PublicKeyCredential !== undefined;
  },

  /**
   * Get WebAuthn status for current user
   */
  getStatus: async () => {
    const response = await api.get('/webauthn/status');
    return response.data;
  },

  /**
   * Get list of registered security keys
   */
  getKeys: async () => {
    const response = await api.get('/webauthn/keys');
    return response.data;
  },

  /**
   * Begin security key registration
   */
  beginRegistration: async () => {
    const response = await api.post('/webauthn/register/begin');
    return response.data;
  },

  /**
   * Register a new security key
   * @param {string} deviceName - User-friendly name for the key
   */
  registerKey: async (deviceName = 'Security Key') => {
    // Step 1: Get registration options from server
    const { options } = await webauthnService.beginRegistration();

    // Step 2: Parse options for browser
    const publicKeyOptions = parseCreationOptions(options);

    // Step 3: Create credential with browser API
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      throw new Error('Failed to create credential');
    }

    // Step 4: Send credential to server for verification
    const formattedCredential = formatCredentialForServer(credential);
    const response = await api.post('/webauthn/register/complete', {
      credential: formattedCredential,
      device_name: deviceName,
    });

    return response.data;
  },

  /**
   * Begin security key authentication (for login)
   * @param {Object} params - User identifier (email, username, or user_id)
   */
  beginAuthentication: async (params) => {
    // Support passing just email/username as string for backward compatibility
    const data = typeof params === 'string'
      ? { username: params }  // Assume string is username for staff login
      : params;
    const response = await api.post('/webauthn/authenticate/begin', data);
    return response.data;
  },

  /**
   * Authenticate with security key
   * @param {Object|string} identifier - User identifier (email, username, or user_id object)
   */
  authenticate: async (identifier) => {
    // Step 1: Get authentication options from server
    const { options, user_id } = await webauthnService.beginAuthentication(identifier);

    // Step 2: Parse options for browser
    const publicKeyOptions = parseRequestOptions(options);

    // Step 3: Get credential from user's security key
    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      throw new Error('Authentication cancelled');
    }

    // Step 4: Send credential to server for verification
    const formattedCredential = formatCredentialForServer(credential);
    const response = await api.post('/webauthn/authenticate/complete', {
      user_id: user_id,
      credential: formattedCredential,
    });

    return response.data;
  },

  /**
   * Delete a security key
   * @param {number} keyId - Key ID to delete
   */
  deleteKey: async (keyId) => {
    const response = await api.delete(`/webauthn/keys/${keyId}`);
    return response.data;
  },

  /**
   * Rename a security key
   * @param {number} keyId - Key ID to rename
   * @param {string} name - New name
   */
  renameKey: async (keyId, name) => {
    const response = await api.patch(`/webauthn/keys/${keyId}`, { name });
    return response.data;
  },
};

export default webauthnService;
